// @ts-nocheck
import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguageContext } from "../context/languageContext";
import { Footer } from "../components/Footer";

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const { t } = useLanguageContext();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
            {/* Decorative Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-indigo-100/40 to-transparent rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-100/30 to-transparent rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="group inline-flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm text-indigo-600 font-medium rounded-xl hover:bg-indigo-50 border border-indigo-100/50 shadow-sm mb-8 transition-all"
                >
                    <svg className="h-4 w-4 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t("Back")}
                </button>

                {/* Main Content Card */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{t("Privacy Policy")}</h1>
                        <p className="text-purple-100 text-sm">Last Updated: February 10, 2026</p>
                    </div>

                    {/* Content */}
                    <div className="px-6 sm:px-8 py-8 space-y-8">
                        {/* Introduction */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Introduction")}
                            </h2>
                            <p className="text-slate-600 leading-relaxed">
                                At Vivas Guest Experience, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our digital ordering and room service platform.
                            </p>
                        </section>

                        {/* Information We Collect */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Information We Collect")}
                            </h2>
                            <div className="space-y-4">
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                    <h3 className="font-semibold text-slate-900 mb-2">Personal Information</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        We collect information you provide directly, such as your name, room number, contact details, and payment information when you place orders or request services.
                                    </p>
                                </div>
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                    <h3 className="font-semibold text-slate-900 mb-2">Usage Information</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        We automatically collect information about your interactions with our platform, including your IP address, browser type, device information, pages visited, and time spent on our service.
                                    </p>
                                </div>
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                    <h3 className="font-semibold text-slate-900 mb-2">Order Information</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        Details about your orders, including items selected, special requests, delivery preferences, and order history.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* How We Use Your Information */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("How We Use Your Information")}
                            </h2>
                            <div className="space-y-3">
                                <div className="flex gap-3">
                                    <svg className="flex-shrink-0 w-5 h-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-slate-600 leading-relaxed">Process and fulfill your orders and service requests</p>
                                </div>
                                <div className="flex gap-3">
                                    <svg className="flex-shrink-0 w-5 h-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-slate-600 leading-relaxed">Communicate with you about your orders and services</p>
                                </div>
                                <div className="flex gap-3">
                                    <svg className="flex-shrink-0 w-5 h-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-slate-600 leading-relaxed">Improve and personalize your experience on our platform</p>
                                </div>
                                <div className="flex gap-3">
                                    <svg className="flex-shrink-0 w-5 h-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-slate-600 leading-relaxed">Process payments and prevent fraudulent transactions</p>
                                </div>
                                <div className="flex gap-3">
                                    <svg className="flex-shrink-0 w-5 h-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-slate-600 leading-relaxed">Send you updates, promotional materials, and special offers (with your consent)</p>
                                </div>
                                <div className="flex gap-3">
                                    <svg className="flex-shrink-0 w-5 h-5 text-purple-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-slate-600 leading-relaxed">Comply with legal obligations and protect our rights</p>
                                </div>
                            </div>
                        </section>

                        {/* Data Security */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Data Security")}
                            </h2>
                            <p className="text-slate-600 leading-relaxed mb-3">
                                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                            </p>
                            <ul className="space-y-2 ml-6">
                                <li className="text-slate-600 leading-relaxed list-disc">Encryption of data in transit and at rest</li>
                                <li className="text-slate-600 leading-relaxed list-disc">Regular security assessments and updates</li>
                                <li className="text-slate-600 leading-relaxed list-disc">Access controls and authentication mechanisms</li>
                                <li className="text-slate-600 leading-relaxed list-disc">Employee training on data protection</li>
                            </ul>
                        </section>

                        {/* Your Rights */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Your Rights")}
                            </h2>
                            <p className="text-slate-600 leading-relaxed mb-3">
                                You have the right to:
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                                    <p className="text-sm font-semibold text-purple-900">Access Your Data</p>
                                    <p className="text-xs text-purple-700 mt-1">Request a copy of your personal information</p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                                    <p className="text-sm font-semibold text-purple-900">Correct Your Data</p>
                                    <p className="text-xs text-purple-700 mt-1">Update inaccurate or incomplete information</p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                                    <p className="text-sm font-semibold text-purple-900">Delete Your Data</p>
                                    <p className="text-xs text-purple-700 mt-1">Request deletion of your personal information</p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                                    <p className="text-sm font-semibold text-purple-900">Opt-Out</p>
                                    <p className="text-xs text-purple-700 mt-1">Unsubscribe from marketing communications</p>
                                </div>
                            </div>
                        </section>

                        {/* Cookies */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Cookies and Tracking")}
                            </h2>
                            <p className="text-slate-600 leading-relaxed">
                                We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and deliver personalized content. You can control cookie preferences through your browser settings, though some features may not function properly if cookies are disabled.
                            </p>
                        </section>

                        {/* Third-Party Services */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Third-Party Services")}
                            </h2>
                            <p className="text-slate-600 leading-relaxed">
                                We may share your information with trusted third-party service providers who assist us in operating our platform, processing payments, and delivering services. These providers are contractually obligated to protect your information and use it only for the purposes we specify.
                            </p>
                        </section>

                        {/* Changes to Privacy Policy */}
                        <section>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-8 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full"></span>
                                {t("Changes to This Policy")}
                            </h2>
                            <p className="text-slate-600 leading-relaxed">
                                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after such modifications constitutes acceptance of the updated policy.
                            </p>
                        </section>

                        {/* Contact Information */}
                        <section className="bg-gradient-to-br from-purple-50/50 to-indigo-50/30 rounded-2xl p-6 border border-purple-100/50">
                            <h2 className="text-xl font-bold text-slate-900 mb-3">{t("Contact Us")}</h2>
                            <p className="text-slate-600 leading-relaxed mb-4">
                                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
                            </p>
                            <div className="space-y-2 text-sm">
                                <p className="text-slate-700"><span className="font-semibold">Email:</span> privacy@vivasexperience.com</p>
                                <p className="text-slate-700"><span className="font-semibold">Phone:</span> +355 69 521 7675</p>
                                <p className="text-slate-700"><span className="font-semibold">Address:</span> Vivas Hotel, Tirana, Albania</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
